import { all, takeLatest, select, put, call } from 'redux-saga/effects';

import {
  types,
  fetchRecordCategoriesSuccess,
  fetchRecordCategoriesFailure,
  changeRecordCategory,
  getSelectedCategory,
  fetchScoreRecordListFailure,
  fetchScoreRecordListSuccess,
  fetchRecordDetailFailure,
  fetchRecordDetailSuccess,
  toggleConceptCheckedFailure,
  toggleConceptCheckedSuccess,
  getRecord,
  getSelectedType,
  changeRecordCategoryType,
  getStudentId,
  setStudentName,
} from '@blueberry/redux/scoreRecord';
import { getSelectedSubjectId } from '@blueberry/redux/dashboard';
import { showAlertBanner } from '@blueberry/redux/alertBanner';
import { goToDashboardScoreRecordList } from '@blueberry/redux/navigation';
import { getRole } from '@blueberry/redux/profile';
import { TUTOR } from '@snapask/common-components/dist/constants/userRoles';

import { SCHOOL } from '@blueberry/constants/scoreRecordCategories';
import { ALERT_ERROR_THEME } from '@blueberry/constants/alertBannerThemes';

import {
  fetchSchoolScoreRecords,
  fetchRecordDetail,
  patchConceptChecked,
  fetchScoreTypes,
  fetchScoreTypesByTutor,
  fetchRecoreDetailGraph,
  fetchSchoolScoreRecordsByTutor,
  fetchRecordDetailByTutor,
  fetchRecoreDetailGraphByTutor,
} from '@blueberry/services/apis';
import { fetchDropDownDataFlow } from '@blueberry/sagas/tutorDashboard';

export function* fetchRecordCategoriesFlow() {
  try {
    const role = yield select(getRole);
    const studentId = yield select(getStudentId);
    const subjectId = yield select(getSelectedSubjectId);
    let categories = [];

    // STUDENT and TUTOR has different api endpoints.
    if (role === TUTOR) {
      categories = yield call(fetchScoreTypesByTutor, { subjectId, studentId });
    } else {
      categories = yield call(fetchScoreTypes, subjectId);
    }

    yield put(fetchRecordCategoriesSuccess(categories));

    // clear type
    yield put(changeRecordCategoryType(''));

    if (categories.length > 0) {
      const [defaultCategory] = categories;
      const [defaultAssessmentType] = defaultCategory.assessment_types;
      // set default Category
      yield put(changeRecordCategory(defaultCategory));
      // set default type which is according to category
      yield put(changeRecordCategoryType(defaultAssessmentType));
    }
  } catch (err) {
    yield put(fetchRecordCategoriesFailure(err));
  }
}

export function* fetchScoreRecordListFlow() {
  try {
    const role = yield select(getRole);
    const studentId = yield select(getStudentId);
    const subjectId = yield select(getSelectedSubjectId);
    const { name, year } = yield select(getSelectedCategory);
    let assessmentType = name;
    let response = {};

    if (name === SCHOOL) {
      const type = yield select(getSelectedType);
      assessmentType = `${assessmentType}_${type}`;
    }

    // STUDENT and TUTOR has different api endpoints.
    if (role === TUTOR) {
      response = yield call(fetchSchoolScoreRecordsByTutor, {
        subjectId,
        assessmentType,
        year,
        studentId,
      });
    } else {
      response = yield call(fetchSchoolScoreRecords, {
        subjectId,
        assessmentType,
        year,
      });
    }

    yield put(fetchScoreRecordListSuccess(response));
  } catch (err) {
    yield put(fetchScoreRecordListFailure(err));
  }
}

export function* fetchRecordDetailFlow(action) {
  try {
    const { subjectId, recordId, studentId } = action.payload;
    const role = yield select(getRole);
    let response = {};
    let series = {};

    // STUDENT and TUTOR has different api endpoints.
    if (role === TUTOR) {
      yield call(fetchDropDownDataFlow);
      const [detail, { series: graphData }] = yield all([
        call(fetchRecordDetailByTutor, {
          subjectId,
          recordId,
          studentId,
        }),
        call(fetchRecoreDetailGraphByTutor, {
          subjectId,
          recordId,
          studentId,
        }),
      ]);

      response = detail.assessment_result;
      series = graphData;
      yield put(setStudentName(detail.actual_name || detail.username));
    } else {
      const [detail, { series: graphData }] = yield all([
        call(fetchRecordDetail, {
          subjectId,
          recordId,
        }),
        call(fetchRecoreDetailGraph, {
          subjectId,
          recordId,
        }),
      ]);

      response = detail;
      series = graphData;
    }

    yield put(fetchRecordDetailSuccess(response, series));
  } catch (err) {
    yield put(fetchRecordDetailFailure(err));
    yield put(
      showAlertBanner({
        theme: ALERT_ERROR_THEME,
        message: 'common.dataNotExist',
      })
    );

    yield put(goToDashboardScoreRecordList());
  }
}

export function* toggleConceptCheckedFlow(action) {
  try {
    const { conceptId, subtopicId, isChecked } = action.payload;

    yield call(patchConceptChecked, {
      subtopicId,
      conceptId,
      isChecked: !isChecked,
    });

    const record = yield select(getRecord);

    const concept = record.summary.find(
      p => p.id === conceptId && p.subtopic_id === subtopicId
    );
    concept.is_checked = !isChecked;

    yield put(toggleConceptCheckedSuccess(record));
  } catch (err) {
    yield put(toggleConceptCheckedFailure(err));
  }
}

export default function*() {
  yield all([
    takeLatest(types.FETCH_RECORD_CATEGORIES, fetchRecordCategoriesFlow),
    takeLatest(types.FETCH_SCORE_RECORD_LIST, fetchScoreRecordListFlow),
    takeLatest(types.FETCH_RECORD_DETAIL, fetchRecordDetailFlow),
    takeLatest(types.TOGGLE_RECORD_CONCEPT_CHECKED, toggleConceptCheckedFlow),
  ]);
}



// WEBPACK FOOTER //
// ./app/sagas/scoreRecord.js